
import { PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnDestroy, OnInit, viewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from '@angular/material/sidenav';
import { ProjectAssetMappingService } from '@app/cem/project/content/project-asset-mapping/project-asset-mapping.service';
import { AssetMappingConfiguration, IModuleAllocation } from '@app/cem/project/models/asset-mapping-configuration';
import { ProjectAssetMappingBulkService } from '@app/cem/project/services/project-asset-mapping-bulk.service';
import { ErrorService } from '@app/common/services/error.service';
import { PxdAgGridOptions } from '@app/shared/aggrid/pxd-ag-grid-options';
import { ProjectSubTypeId, ProjectTypeId } from '@app/shared/data/project-enums';
import { IProjectAssetMapping } from '@app/shared/interfaces';
import { Project, ProjectWellAssetMappingView, TankBattery } from '@app/shared/models';
import { WellsProjectAssetMappingView } from '@app/shared/models/wells-project-asset-mapping-view';
import { calculateSplitPercentages, round } from '@app/shared/utilities/math-utilities';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, GridReadyEvent, SelectionChangedEvent } from 'ag-grid-community';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { assetMappingMultiWellAvailablesGridOptions, assetMappingMultiWellMappedGridOptions, assetMappingMultiWellMappedTankBatteryGridOptions } from './multi-well-grid-options';

@Component({
  selector: 'app-project-asset-mapping-multi-well',
  templateUrl: './project-asset-mapping-multi-well.component.html',
  styleUrls: ['./project-asset-mapping-multi-well.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    ButtonDirective,
    AgGridAngular,
    DialogComponent,
    DialogActionsComponent,
    PercentPipe,
  ],
})
export class ProjectAssetMappingMultiWellComponent implements OnInit, OnDestroy {
  private bulkAssetMappingService = inject(ProjectAssetMappingBulkService);
  private errorService = inject(ErrorService);
  private cd = inject(ChangeDetectorRef);

  private projectTotalAllocationSubscription: Subscription;
  private modulesToReloadSubscription: Subscription;
  private modulesToSaveChangesSubscription: Subscription;
  private modulestoCancelChangesSubscription: Subscription;
  private totalAllocationSubscription: Subscription;
  private mappedWellsChangedSubscription: Subscription;
  private tankBatteryAssetChangedSubscription: Subscription;
  private tankBatteryAssetSavedSubscription: Subscription;
  private totalAllocation: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private hasPendingChange: boolean = false;
  private initialLoad: boolean = true;
  private totalProjectAllocationPercentage: number;
  showToggleAllocation: boolean;
  inactiveAssetsMessage: boolean = false;
  totalMappedWells: number;
  totalAllocationPercentage: number = 0;
  allocationAllowed: boolean = false;
  moduleIsEditable: boolean = false;
  openedWarning: boolean = false;
  newWellId: number;

  selectedTBAssetForm: UntypedFormGroup = null;
  tankBatteryAsset: TankBattery = null;

  existingSdeFacilityIdOverrides: number[];
  assetChanged: boolean = false;

  readonly projectId = input<number>(undefined);
  readonly project = input<Project>(undefined);
  readonly projectTypeId = input<number>(undefined);
  readonly projectSubTypeId = input<number>(undefined);
  readonly assetMappingConfig = input<AssetMappingConfiguration>(undefined);
  readonly projectAssetMappingService = input<ProjectAssetMappingService>(undefined);

  private readonly agAvailablesGrid = viewChild<AgGridAngular>("availablesGrid");
  private readonly agMappedGrid = viewChild<AgGridAngular>("mappedGrid");

  availablesGridOptions: PxdAgGridOptions = assetMappingMultiWellAvailablesGridOptions;
  mappedGridOptionsHandler: PxdAgGridOptions = assetMappingMultiWellMappedGridOptions;

  availableWells: WellsProjectAssetMappingView[] = [];
  mappedWellsChanged$: Subject<boolean> = new Subject<boolean>();
  mappedWells: ProjectWellAssetMappingView[] = [];

  editedMappedWells: Map<number, ProjectWellAssetMappingView> = new Map<number, ProjectWellAssetMappingView>();
  deletedMappedWells: Map<number, ProjectWellAssetMappingView> = new Map<number, ProjectWellAssetMappingView>();

  initialMappedWellsLoaded: boolean = false;
  initialAvailableWellsLoaded: boolean = false;
  mappedWellsGridReady: boolean = false;
  availableWellsGridReady: boolean = false;
  disableAddSelected: boolean = true;
  disableRemoveMapped: boolean = true;
  disableAllocateEvenly: boolean = true;

  async ngOnInit() {
    if (this.isValidTankBatteryProjectType()) {
      this.mappedGridOptionsHandler = assetMappingMultiWellMappedTankBatteryGridOptions;
      this.tankBatteryAssetChangedSubscription = this.projectAssetMappingService().tbAssetChanged.subscribe(async (x: UntypedFormGroup) => {
        await this.tankBatteryAssetChanged(x);
      });
      this.tankBatteryAssetSavedSubscription = this.projectAssetMappingService().tbAssetSaved.subscribe(async () => {
        this.getMappedWellsAndTryLoadGrid();
      })
    }
    const assetMappingConfig = this.assetMappingConfig();
    this.moduleIsEditable = assetMappingConfig.ModuleProperties.IsMappingEditable && assetMappingConfig.ModuleProperties.IsMappingEditable;
    const assetMappingConfigValue = this.assetMappingConfig();
    this.allocationAllowed = assetMappingConfigValue.IsAllowAllocation && assetMappingConfigValue.IsAllowAllocation;
    this.setupSubscriptions();
    await Promise.all([this.getMappedWellsAndTryLoadGrid(), this.getAvailableWellsAndTryLoadGrid()]);
  }

  ngOnDestroy() {
    this.projectTotalAllocationSubscription.unsubscribe();
    this.modulesToReloadSubscription.unsubscribe();
    this.modulesToSaveChangesSubscription.unsubscribe();
    this.modulestoCancelChangesSubscription.unsubscribe();
    this.totalAllocationSubscription.unsubscribe();
    this.mappedWellsChangedSubscription.unsubscribe();
    if (this.tankBatteryAssetChangedSubscription) {
      this.tankBatteryAssetChangedSubscription.unsubscribe();
    }
    if (this.tankBatteryAssetSavedSubscription) {
      this.tankBatteryAssetSavedSubscription.unsubscribe();
    }
  }

  private async tankBatteryAssetChanged(x: UntypedFormGroup) {
    if (!x || !x.controls || !x.controls.Asset || !x.controls.Asset.value) {
      return;
    }

    if (this.selectedTBAssetForm == null) {
      this.selectedTBAssetForm = x;
      let asset = await this.projectAssetMappingService().findTankBattery(this.selectedTBAssetForm.controls.Asset.value.AssetId);
      this.tankBatteryAsset = asset;
      return;
    }

    this.assetChanged = true;
    this.selectedTBAssetForm = x;
    let asset = await this.projectAssetMappingService().findTankBattery(this.selectedTBAssetForm.controls.Asset.value.AssetId);
    this.tankBatteryAsset = asset;
    return this.validateSDEOverride();
  }

  private setupSubscriptions() {
    this.mappedWellsChangedSubscription = this.mappedWellsChanged$.subscribe(() => {
      if (this.mappedWells.length > 0 || this.deletedMappedWells.size > 0) {
        this.disableAllocateEvenly = false;
        let currentTotalMappedWells = this.totalMappedWells;
        let currentTotalAllocation = this.totalAllocation.getValue();
        let allocation = 0;
        this.mappedWells.forEach(a => { allocation += a.PercentAllocation });
        this.totalMappedWells = this.mappedWells.length;
        this.existingSdeFacilityIdOverrides = [...new Set(this.mappedWells.filter(x => x.SdeFacilityIdOverride > 0).map(x => x.SdeFacilityIdOverride))];
        if ((currentTotalAllocation !== allocation || currentTotalMappedWells !== this.totalMappedWells) && this.initialLoad === false) {
          this.hasPendingChange = true;
        }

        this.initialLoad = false;
        this.totalAllocation.next(allocation);
        this.projectAssetMappingService().setModuleAllocation(
          {
            assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
            moduleId: this.assetMappingConfig().ModuleId,
            allocationPercentage: allocation || 0,
            hasAssets: this.totalMappedWells > 0 ? true : false,
            isActiveToggle: this.totalMappedWells > 0 ? true : false,
            isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
            hasPendingChange: this.hasPendingChange
          }
        );
      } else {
        this.disableAllocateEvenly = true;
      }
    });

    this.totalAllocationSubscription = this.totalAllocation.subscribe(t => {
      let allocationPercentage = this.totalAllocation.getValue();
      this.totalAllocationPercentage = allocationPercentage;
      this.setToggleModule(this.totalMappedWells > 0 ? true : false);
      this.projectAssetMappingService().setModuleAllocation(
        {
          assetMappingConfigurationId: this.assetMappingConfig().AssetMappingConfigurationId,
          moduleId: this.assetMappingConfig().ModuleId,
          hasAssets: this.totalMappedWells > 0 ? true : false,
          isActiveToggle: this.totalMappedWells > 0 ? true : false,
          allocationPercentage: allocationPercentage || 0,
          isToggle: this.assetMappingConfig().ModuleProperties.IsToggleModule,
          hasPendingChange: this.hasPendingChange
        }
      );
    });

    this.modulesToReloadSubscription = this.projectAssetMappingService().getModulesToReload()
      .subscribe((event) => {
        event.map(async (data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            await this.clearAllocation(data);
          }
        });
      });

    this.modulesToSaveChangesSubscription = this.projectAssetMappingService().getModulesToSaveChanges()
      .subscribe((event) => {
        event.map(async (data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            await this.saveProjectAssetMapping();
            this.editedMappedWells.clear();
            this.deletedMappedWells.clear();
          }
        });
      });

    this.modulestoCancelChangesSubscription = this.projectAssetMappingService().getModulesToCancelChanges()
      .subscribe((event) => {
        event.map(async (data) => {
          const assetMappingConfig = this.assetMappingConfig();
          if (data && assetMappingConfig.AssetMappingConfigurationId && data.assetMappingConfigurationId === assetMappingConfig.AssetMappingConfigurationId) {
            await this.cancelChanges();
          }
        });
      });

    this.projectTotalAllocationSubscription = this.projectAssetMappingService().getProjectTotalAllocation().subscribe(x => {
      this.totalProjectAllocationPercentage = x;
    });
  }

  onWarningClose() {
    this.openedWarning = false;
    this.agAvailablesGrid().api.deselectAll();
    if (this.assetChanged) {
      setTimeout(() => {
        this.tankBatteryAsset = null;
        this.selectedTBAssetForm.controls.Asset.setValue(null);
        this.assetChanged = false;
        this.cd.detectChanges();
      })

    }
  }

  onWarningSave() {
    this.confirmSelection();
    this.openedWarning = false;
  }

  validateSDEOverride() {
    let wellSdeFacilityIdOverrides = (this.agAvailablesGrid().api.getSelectedRows() as WellsProjectAssetMappingView[]).filter(x => x.SdeFacilityIdOverride > 0).map(x => x.SdeFacilityIdOverride);
    wellSdeFacilityIdOverrides = [...new Set(wellSdeFacilityIdOverrides.concat(this.existingSdeFacilityIdOverrides))];
    if (this.isValidTankBatteryProjectType() && this.tankBatteryAsset && wellSdeFacilityIdOverrides.filter(x => x !== this.tankBatteryAsset.TankBatteryId).length > 0) {
      setTimeout(() => {
        this.openedWarning = true;
        this.cd.detectChanges();
      })
    }
    else
      this.confirmSelection();
  }

  private isValidTankBatteryProjectType() {
    const projectSubTypeId = this.projectSubTypeId();
    return (this.projectTypeId() == ProjectTypeId.TankBattery &&
      (projectSubTypeId == ProjectSubTypeId.Expansion || projectSubTypeId == ProjectSubTypeId.InitialBuild))
  }

  private async clearAllocation(module: IModuleAllocation) {
    this.hasPendingChange = false;
    await Promise.all([this.getAvailableWellsAndTryLoadGrid(), this.getMappedWellsAndTryLoadGrid()]);
    this.showToggleAllocation = true;
    this.projectAssetMappingService().setModuleAllocation(module);
  }

  private setToggleModule(hasAsset: boolean) {
    if (this.assetMappingConfig().ModuleProperties.IsToggleModule) {
      this.showToggleAllocation = !hasAsset;
    }
  }

  private async saveProjectAssetMapping() {
    try {
      //Convert objects to ProjectAssetMappings for save
      let toDeleteArray = Array.from(this.deletedMappedWells.values()).map(x => ({
        ProjectAssetMappingId: x.ProjectAssetMappingId,
        PercentAllocation: x.PercentAllocation,
        AssetId: x.AssetId,
        ProjectId: x.ProjectId
      } as IProjectAssetMapping));
      let toUpdateArray = Array.from(this.editedMappedWells.values()).map(x => ({
        ProjectAssetMappingId: x.ProjectAssetMappingId,
        AssetId: x.AssetId,
        ProjectId: x.ProjectId,
        PercentAllocation: x.PercentAllocation,
        IsConstructedAsset: x.IsConstructedAsset
      } as IProjectAssetMapping));
      let toCreateArray = this.mappedWells
        .filter(x => x.ProjectAssetMappingId <= 0)
        .map(x => ({
          ProjectAssetMappingId: 0,
          AssetId: x.AssetId,
          ProjectId: x.ProjectId,
          PercentAllocation: x.PercentAllocation,
          IsConstructedAsset: x.IsConstructedAsset
        } as IProjectAssetMapping));
      await this.bulkAssetMappingService.saveChanges$(toDeleteArray, toUpdateArray, [], toCreateArray).toPromise();
    } catch (err) {
      this.errorService.error("There was an error saving AssetAllocation for Multi Assets", err);
    }
    this.hasPendingChange = false;
    await Promise.all([this.getMappedWellsAndTryLoadGrid(), this.getAvailableWellsAndTryLoadGrid()]);
  }

  toggleAllocationModule() {
    this.showToggleAllocation = false;
    this.projectAssetMappingService().toggleModuleAllocation(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId);
  }

  onAvailablesSelectionChanged(params: SelectionChangedEvent) {
    if (this.agAvailablesGrid().api.getSelectedRows().length > 0) {
      this.disableAddSelected = false;
    } else {
      this.disableAddSelected = true;
    }
  }

  onMappedCellValueChanged(params: CellValueChangedEvent): void {
    if (params.oldValue === params.newValue) {
      return;
    }
    let delta = params.newValue - params.oldValue;
    let allocation = this.totalAllocation.getValue();
    this.hasPendingChange = true;
    this.totalAllocation.next(allocation + delta);
    if (params.data.ProjectAssetMappingId > 0) {
      this.editedMappedWells.set(params.data.ProjectAssetMappingId, params.data);
    }
  }

  onMappedSelectionChanged(params: SelectionChangedEvent) {
    if (this.agMappedGrid().api.getSelectedRows().length > 0) {
      this.disableRemoveMapped = false;
    } else {
      this.disableRemoveMapped = true;
    }
  }

  getMappedWellsAndTryLoadGrid() {
    this.inactiveAssetsMessage = false;
    this.projectAssetMappingService().findMappedWells(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId)
      .then(x => {
        this.mappedWells = x;
        this.initialMappedWellsLoaded = true;
        this.hasPendingChange = false;
        this.mappedWellsChanged$.next(true);
        if (this.mappedWells.some(well => well.IsActive === false)) {
          this.inactiveAssetsMessage = true;
        }
        this.loadMappedGrid();
      })
      .catch((error) => {
        this.errorService.handleHTTPErrorWithMessage(error, "There was an error getting ProjectAssetMappings for Asset Allocation");
      });
  }

  onMappedGridReady(params: GridReadyEvent): void {
    this.mappedWellsGridReady = true;
    this.loadMappedGrid();
  }

  loadMappedGrid() {
    const agMappedGrid = this.agMappedGrid();
    if (this.initialMappedWellsLoaded && this.mappedWellsGridReady
      && agMappedGrid?.api) {
      this.newWellId = 0;
      agMappedGrid.api.setGridOption('rowData', this.mappedWells);
    }
  }

  getAvailableWellsAndTryLoadGrid() {
    this.projectAssetMappingService().findAvailableWells(this.projectId(), this.assetMappingConfig().AssetMappingConfigurationId)
      .then(x => {
        this.availableWells = x;
        this.initialAvailableWellsLoaded = true;
        this.loadAvailablesGrid();
      })
      .catch((error) => {
        this.errorService.handleHTTPErrorWithMessage(error, "There was an error getting Well Options for Asset Allocation");
      });
  }

  onAvailablesGridReady(params: GridReadyEvent): void {
    this.availableWellsGridReady = true;
    this.loadAvailablesGrid();
  }

  loadAvailablesGrid() {
    const agAvailablesGrid = this.agAvailablesGrid();
    if (this.initialAvailableWellsLoaded && this.availableWellsGridReady
      && agAvailablesGrid?.api) {
      agAvailablesGrid.api.setGridOption('rowData', this.availableWells);
    }
  }

  splitAllocation() {
    let totalAllocationPct = this.totalAllocation.getValue();
    let pct = round(this.totalProjectAllocationPercentage - totalAllocationPct, 6);
    let modulePercentage = 1 - pct;
    if (modulePercentage < 0) {
      return;
    }

    let splitPercentages = calculateSplitPercentages(modulePercentage, this.totalMappedWells, 6);
    let allocation = 0;
    let changes = this.mappedWells.map((x, ix) => {
      x.PercentAllocation = splitPercentages[ix];
      allocation = allocation + x.PercentAllocation;
      if (x.ProjectAssetMappingId > 0) {
        this.editedMappedWells.set(x.ProjectAssetMappingId, x);
      }
      return x;
    });
    this.agMappedGrid().api.applyTransaction({ update: changes })
    this.hasPendingChange = true;
    this.totalAllocation.next(allocation);
  }

  addWellToMapping() {
    if (this.projectTypeId() == ProjectTypeId.TankBattery) {
      return this.validateSDEOverride();
    }
    this.confirmSelection();
  }

  confirmSelection() {
    let wellsToAdd: ProjectWellAssetMappingView[] = [];
    let selectedAvailableWells = this.agAvailablesGrid().api.getSelectedRows() as ProjectWellAssetMappingView[];
    if (selectedAvailableWells.length > 0) {
      selectedAvailableWells.forEach(item => {
        const assetMappingConfig = this.assetMappingConfig();
        let newMapping: ProjectWellAssetMappingView = {
          ProjectAssetMappingId: this.newWellId--,
          PercentAllocation: 0,
          IsConstructedAsset: assetMappingConfig.IsConstructedModule ? assetMappingConfig.IsConstructedModule : false,
          ProjectId: this.projectId(),
          WellId: item.WellId,
          WellName: item.AssetName,
          Ssn: item.Ssn,
          OWID: item.OWID,
          EstimatedNetWorkingInterest: item.EstimatedNetWorkingInterest,
          PreliminaryNetWorkingInterest: item.PreliminaryNetWorkingInterest,
          AssetId: item.AssetId,
          AssetTypeId: item.AssetTypeId,
          AssetType: item.AssetType,
          AssetSuperTypeId: item.AssetSuperTypeId,
          TankBatteryAreaId: item.TankBatteryAreaId,
          AssetName: item.AssetName,
          AssetTeamId: item.AssetTeamId,
          Operator: item.Operator,
          IsInsideCityLimits: item.IsInsideCityLimits,
          CostCenterId: item.CostCenterId,
          CostCenterNumber: item.CostCenterNumber,
          CostCenter: item.CostCenter,
          ActualGrossWorkingInterest: item.ActualGrossWorkingInterest,
          TargetFormation: item.TargetFormation,
          Pad: "",
          County: item.County,
          State: item.State,
          ScenarioId: item.ScenarioId,
          PutOnProductionDate: null,
          TankBattery: null,
          SdeFacilityIdOverride: null,
          ActualDrillGroupReference: item.ActualDrillGroupReference,
          IsActive: item.IsActive
        };
        wellsToAdd.push(newMapping);
      });

      this.agMappedGrid().api.applyTransaction({ add: wellsToAdd });
      this.mappedWells = this.mappedWells.concat(wellsToAdd);
      this.hasPendingChange = true;
      this.mappedWellsChanged$.next(true);
      this.agAvailablesGrid().api.applyTransaction({ remove: selectedAvailableWells });
      this.agAvailablesGrid().api.deselectAll();
    }
  }

  removeMappedWells() {
    let rowsToRemove = this.agMappedGrid().api.getSelectedRows() as ProjectWellAssetMappingView[];
    rowsToRemove.filter(x => x.ProjectAssetMappingId > 0).forEach(x => {
      this.deletedMappedWells.set(x.ProjectAssetMappingId, x);
      this.editedMappedWells.delete(x.ProjectAssetMappingId)
    });
    this.mappedWells = this.mappedWells.filter(x => !rowsToRemove.includes(x));
    this.agMappedGrid().api.applyTransaction({ remove: rowsToRemove });
    this.agAvailablesGrid().api.applyTransaction({ add: rowsToRemove });
    this.hasPendingChange = true;
    this.mappedWellsChanged$.next(true);
  }

  cancelAvailableWellsSelection() {
    this.agAvailablesGrid().api.deselectAll();
  }

  private async cancelChanges() {
    this.agAvailablesGrid().api.deselectAll();
    this.agMappedGrid().api.deselectAll();

    this.deletedMappedWells = new Map<number, ProjectWellAssetMappingView>();
    this.editedMappedWells = new Map<number, ProjectWellAssetMappingView>();

    this.hasPendingChange = false;
    await Promise.all([this.getAvailableWellsAndTryLoadGrid(), this.getMappedWellsAndTryLoadGrid()]);
  }
}